<template>
  <div class="pull-model">
    <div class="pull-model__view-details">
      <h1>RedStone Sources</h1>
      <p>
        Access real-time token pricing from multiple trusted sources with
        reliable infrastructure for accurate DeFi applications.
      </p>
    </div>
    <div>
      <div class="pull-model__status mb-4" style="width: 120px">
        <span class="feeds__status-text mr-4">
          <div>
            <i class="fa fa-globe inline"></i> Sources:
            <strong v-if="this.sources?.length > 0" class="ml-2">{{
              this.filteredSources.length
            }}</strong>
            <vue-loaders-ball-beat
              v-else
              color="var(--redstone-red-color)"
              scale="0.5"
            />
          </div>
        </span>
      </div>
    </div>
    <b-table
      class="pull-model"
      id="sources-table"
      stacked="md"
      sort-icon-left
      :busy="loading"
      hover
      sortBy="id"
      :items="filteredSources"
      :fields="fields"
    >
      <template #cell(name)="source">
        <div class="source-name">
          <img class="source-logo" :src="source.item.logoURI" />
          <a :href="source.item.url" target="_blank">
            <span class="ml-3">
              {{ source.item.id }}
            </span>
          </a>
        </div>
      </template>
      <template #cell(numberOfFeeds)="source">
        <div class="source-name">
          <span class="ml-3">
            {{ tokenSourceCounts?.[source.item.id] }}
          </span>
        </div>
      </template>
    </b-table>

    <div v-if="showReportsLink" class="report-source-link">
      Data source:
      <a
        target="_blank"
        href="https://github.com/redstone-finance/redstone-reports"
      >
        github.com/redstone-finance/redstone-reports
      </a>
    </div>
  </div>
</template>

<script>
  import axios from "axios";
  import _ from "lodash";
  import sources from "../../../config/sources.json";
  import { mapGetters, mapState } from "vuex";

  export default {
    data() {
      return {
        loading: false,
        showReportsLink: false,
        sourcesReportFromGH: {},
        fields: [
          {
            key: "name",
            label: "Source",
            sortable: true,
          },
          {
            key: "numberOfFeeds",
            label: "Number of Feeds",
            sortable: true,
          },
        ],
      };
    },
    methods: {
      findClosest(id) {
        const searchParts = id.split("-");
        let bestMatch = null;
        let bestScore = 0;

        // Find match with highest number of common parts
        Object.keys(sources).forEach((sourceId) => {
          const sourceParts = sourceId.split("-");

          // Count matching parts
          let score = 0;
          searchParts.forEach((part) => {
            if (sourceParts.includes(part)) {
              score++;
            }
          });

          // Higher weight for matching first part (exchange name)
          if (searchParts[0] === sourceParts[0]) {
            score += 2;
          }

          // Track the best match
          if (score > bestScore) {
            bestScore = score;
            bestMatch = sourceId;
          }
        });

        return bestMatch ? sources[bestMatch] : null;
      },
    },

    computed: {
      ...mapGetters("manifests", ["getSymbolDetails"]),
      ...mapState("layout", ["searchTerm"]),
      tokenSourceCounts() {
        const sourceCounts = {};
        Object.entries(this.getSymbolDetails || {}).forEach(
          ([tokenKey, tokenData]) => {
            if (tokenData.config && tokenData.config.source) {
              tokenData.config.source.forEach((source) => {
                sourceCounts[source] = (sourceCounts[source] || 0) + 1;
              });
            }
          }
        );
        return Object.entries(sourceCounts)
          .sort((a, b) => b[1] - a[1])
          .reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
          }, {});
      },
      sources() {
        const result = [];
        for (const [id, count] of Object.entries(this.tokenSourceCounts)) {
          let details = {};
          if (sources[id] != null) {
            details = sources[id];
          } else {
            details = this.findClosest(id);
          }
          result.push({
            count,
            ...details,
            id,
          });
        }
        return result;
      },
      filteredSources() {
        if (!this.searchTerm) {
          return this.sources;
        }
        
        const searchLower = this.searchTerm.toLowerCase();
        return this.sources.filter(source => {
          if (source.id.toLowerCase().includes(searchLower)) {
            return true;
          }
          return false;
        });
      },
    },
  };
</script>

<style lang="scss">
  #sources-table {
    .source-logo {
      width: 60px;
      height: 60px;
      object-fit: contain;
      border-radius: 50%;
    }
    
    .source-name {
      display: flex;
      align-items: center;
    }
  }
</style>